import React, { useEffect, useMemo, useState } from 'react';
import useHierarchy from '../hooks/useHierarchy';
import useHierarchyCascade, { defaultHierarchySelection } from '../hooks/useHierarchyCascade';
import {
	Box,
	IconButton,
	MenuItem,
	Select,
	Tooltip,
} from '@mui/material';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';


const hierarchyFiltersSx = {
	display: 'flex',
	flexWrap: 'wrap',
	alignItems: 'center',
	gap: '8px',
	width: { xs: '100%', sm: 'auto' },
};

const hierarchyControlSx = {
	width: { xs: '100%', sm: 220 },
	minWidth: { xs: '100%', sm: 220 },
	flex: { xs: '1 1 100%', sm: '0 0 auto' },
};

const clearButtonSx = {
	border: '1px solid #b7c0c7',
	borderRadius: '25px',
	color: '#c62828',
	'&:hover': {
		backgroundColor: '#d3d3d3',
	},
	'&.Mui-disabled': {
		color: '#9e9e9e',
		borderColor: '#d0d0d0',
	},
};


const LocationHierarchyFilterControls = React.memo(function LocationHierarchyFilterControls({onFilterChange}) {

	const { hierarchy } = useHierarchy();
	const [hierarchySelection, setHierarchySelection] = useState(() => ({ ...defaultHierarchySelection }));

	// Update local hierarchy selection state when the global hierarchy changes to ensure options are in sync.
	const {
		organizationOptions,
		regionOptions,
		facilityOptions,
		buildingOptions,
		setOrganization,
		setRegion,
		setFacility,
		setBuilding,
		clearSelection,
		hasSelection,
	} = useHierarchyCascade({
		hierarchy,
		selection: hierarchySelection,
		onSelectionChange: setHierarchySelection,
	});

	// Destructure the current hierarchy selection for easier use in the filter payload.
	const { organization, region, facility, building } = hierarchySelection;

	// Memoize the current filters object to avoid unnecessary re-renders or effect triggers when the selection changes.
	const currentFilters = useMemo(() => ({
		org: organization,
		reg: region,
		fac: facility,
		bldg: building,
	}), [organization, region, facility, building]);

	// Emit the current filters to the parent component whenever they change. This allows
	//  the parent to react to filter changes and update the displayed data accordingly.
	useEffect(() => {
		if (onFilterChange) {
			onFilterChange(currentFilters);
		}
	}, [onFilterChange, currentFilters]);

	return (
		<Box sx={hierarchyFiltersSx}>
			<Select
				id="locations-org"
				value={organization}
				displayEmpty
				size="small"
				sx={hierarchyControlSx}
				onChange={(event) => setOrganization(event.target.value)}
			>
				<MenuItem value=""><em>All Organizations</em></MenuItem>
				{organizationOptions.map((org) => (
					<MenuItem key={org} value={org}>{org}</MenuItem>
				))}
			</Select>
			<Select
				id="locations-reg"
				value={region}
				displayEmpty
				size="small"
				sx={hierarchyControlSx}
				disabled={!organization}
				onChange={(event) => setRegion(event.target.value)}
			>
				<MenuItem value=""><em>All Regions</em></MenuItem>
				{regionOptions.map((reg) => (
					<MenuItem key={reg} value={reg}>{reg}</MenuItem>
				))}
			</Select>
			<Select
				id="locations-fac"
				value={facility}
				displayEmpty
				size="small"
				sx={hierarchyControlSx}
				disabled={!region}
				onChange={(event) => setFacility(event.target.value)}
			>
				<MenuItem value=""><em>All Facilities</em></MenuItem>
				{facilityOptions.map((fac) => (
					<MenuItem key={fac} value={fac}>{fac}</MenuItem>
				))}
			</Select>
			<Select
				id="locations-bld"
				value={building}
				displayEmpty
				size="small"
				sx={hierarchyControlSx}
				disabled={!facility}
				onChange={(event) => setBuilding(event.target.value)}
			>
				<MenuItem value=""><em>All Buildings</em></MenuItem>
				{buildingOptions.map((bld) => (
					<MenuItem key={bld} value={bld}>{bld}</MenuItem>
				))}
			</Select>
			<Tooltip title="Clear hierarchy filters">
				<span>
					<IconButton
						size="small"
						onClick={clearSelection}
						disabled={!hasSelection}
						aria-label="Clear hierarchy filters"
						sx={clearButtonSx}
					>
						<FilterAltOffIcon fontSize="small" />
					</IconButton>
				</span>
			</Tooltip>
		</Box>
	);
});

export default LocationHierarchyFilterControls;
